import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/app/admin/_common/ui/tabs";
import { Badge } from "@/app/admin/_common/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/app/admin/_common/ui/sheet";
import CuoButton from "@/app/admin/_common/CuoButton";
import PopOverLinkCopyButton from "@/app/admin/_common/PopOverLinkCopyButton";
import ShowService from "@/app/_services/ShowService";
import { dayjsShow } from "@/app/_utils/dayjs";
import { publicShowPurchaseUrl } from "@/app/_utils/publicProductUrl";
import {
  resolveShowStripeProductId,
  stripeDashboardProductUrl,
} from "@/app/_utils/stripeDashboard";
import {
  ArrowLeft,
  ChevronDown,
  CreditCard,
  Drama,
  Eye,
  EyeOff,
  History,
  MoreVertical,
  Sparkles,
} from "lucide-react";
import { useSessionAuth } from "@/app/_utils/sessionClient";
import { PERMISSIONS } from "@/app/_utils/permissions";

export const tabTriggerClassName =
  "rounded-none border-b-2 border-transparent bg-transparent px-0 pb-2.5 text-muted-foreground shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none";

const NAV_TABS = [
  { value: "relatedshows", label: "Related Shows", permission: PERMISSIONS.SHOW_WRITE },
  { value: "details", label: "Details", permission: PERMISSIONS.PAGE_SHOWS },
  { value: "live", label: "Live sales", permission: PERMISSIONS.PAGE_SHOWS },
  { value: "lineup", label: "Lineup", permission: PERMISSIONS.PAGE_SHOWS },
  { value: "expenses", label: "Costs", permission: PERMISSIONS.FINANCE_PAGE },
  { value: "guestlist", label: "Guest list", permission: PERMISSIONS.PAGE_SHOWS },
  { value: "masterguestlist", label: "Master list", permission: PERMISSIONS.PAGE_SHOWS },
  { value: "intakelist", label: "Intake list", permission: PERMISSIONS.PAGE_SHOWS },
  { value: "checkouts", label: "Checkouts", permission: PERMISSIONS.SHOW_WRITE },
  { value: "checklist", label: "Checklist", permission: PERMISSIONS.PAGE_SHOWS },
  { value: "settings", label: "Settings", permission: PERMISSIONS.SHOW_WRITE },
];

function formatShowDateTime(showDetails: any): string | null {
  if (!showDetails?.start) return null;

  const isVariantPerformance =
    showDetails.parent_id != null ||
    showDetails.parent != null ||
    Boolean(String(showDetails.variant_name ?? "").trim());

  if (!isVariantPerformance) {
    const children = showDetails?.shows;
    if (Array.isArray(children) && children.length > 1) {
      const sorted = [...children].sort(
        (a, b) => dayjsShow(a.start).valueOf() - dayjsShow(b.start).valueOf()
      );
      const dateLabel = dayjsShow(sorted[0].start).format("dddd, D MMM YYYY");
      const times = sorted.map((s) => dayjsShow(s.start).format("h:mm A"));
      return `${dateLabel} · ${times.join(", ")}`;
    }
  }

  const start = dayjsShow(showDetails.start);
  if (!start.isValid()) return null;
  return `${start.format("dddd, D MMM YYYY")} · ${start.format("h:mm A")}`;
}

export default function ShowDetailHeader({
  showDetails,
}: {
  showDetails: any;
}) {
  const navigate = useNavigate();
  const { can } = useSessionAuth();
  const { pathname } = useLocation();
  const [isPublishing, setIsPublishing] = useState(false);

  const activeTab = NAV_TABS.find((t) => pathname.includes(`/${t.value}`))?.value ?? "details";
  const dateTimeLabel = formatShowDateTime(showDetails);
  // Badge mirrors the Status field on the Details tab: Active or Draft.
  const isActive = showDetails?.status === "active";
  const variantLabel = showDetails?.variant_name;
  const stripeProductId = resolveShowStripeProductId(showDetails);
  const stripeProductUrl = stripeDashboardProductUrl(stripeProductId);
  const relatedShows = showDetails?.shows ?? [];
  const canSwitchRelated =
    !["relatedshows", "masterguestlist"].includes(activeTab) && relatedShows.length > 1;

  const visibleTabs = NAV_TABS.filter((tab) => {
    if (tab.value === "masterguestlist" && !showDetails?.variant_name) return false;
    if (["guestlist", "relatedshows"].includes(tab.value) && showDetails?.historical) return false;
    return can(tab.permission);
  });

  async function handleTogglePublish() {
    if (!showDetails) return;
    setIsPublishing(true);
    try {
      await ShowService.publishShow(showDetails.id, !showDetails.published);
      window.location.reload();
    } finally {
      setIsPublishing(false);
    }
  }

  const titleContent = (
    // my-0 is load-bearing: Tailwind preflight is disabled in this app, so an
    // <h1> keeps the UA default margin (0.67em top and bottom).
    <h1 className="my-0 flex min-w-0 items-center gap-2 truncate py-0.5 text-[21px] font-bold leading-none tracking-tight text-foreground">
      {showDetails?.type === "comedy" ? (
        <Drama className="size-5 flex-none text-foreground" />
      ) : (
        <Sparkles className="size-5 flex-none text-foreground" />
      )}
      {showDetails?.title ?? "Show"}
    </h1>
  );

  const dateAndBadgeContent = (
    <div className="flex flex-wrap items-center gap-3 text-[12.5px] text-muted-foreground">
      {canSwitchRelated ? (
        <DropdownMenu>
          <DropdownMenuTrigger className="inline-flex items-center gap-0.5 truncate hover:text-foreground">
            {[variantLabel, dateTimeLabel].filter(Boolean).join(" · ")}
            <ChevronDown className="size-3.5 flex-none" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {relatedShows.map((show: any) => (
              <DropdownMenuItem
                key={show.id}
                onSelect={() =>
                  navigate(`/admin/shows/${show.id}/${activeTab}`, { replace: true })
                }
              >
                {show.variant_name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        (variantLabel || dateTimeLabel) && (
          <span className="truncate">
            {[variantLabel, dateTimeLabel].filter(Boolean).join(" · ")}
          </span>
        )
      )}
      {showDetails?.historical ? (
        <span className="inline-flex items-center gap-1">
          <History className="size-3.5" />
          Historical
        </span>
      ) : (
        <Badge
          variant="outline"
          className={`gap-1.5 rounded-[7px] font-normal ${
            isActive
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-slate-200 bg-slate-50 text-slate-600"
          }`}
        >
          <span
            className={`size-1.5 flex-none rounded-full ${
              isActive ? "bg-emerald-500" : "bg-slate-400"
            }`}
          />
          {isActive ? "Active" : "Draft"}
        </Badge>
      )}
    </div>
  );

  const mobileActionsSheet = !showDetails?.historical && (
    <Sheet>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label="Show actions"
          className="inline-flex size-[30px] flex-none items-center justify-center rounded-lg border border-input bg-card text-foreground transition-colors hover:bg-accent hover:text-accent-foreground md:hidden"
        >
          <MoreVertical className="size-4 shrink-0" size={16} />
        </button>
      </SheetTrigger>
      <SheetContent side="bottom" className="rounded-t-xl bg-white p-0 md:hidden">
        <div className="flex flex-col pb-6 pt-6">
          <PopOverLinkCopyButton
            fullWidthRow
            title="Copy link"
            url={publicShowPurchaseUrl({
              slug: showDetails?.slug,
              parent_slug: showDetails?.parent_slug,
              id: showDetails?.id,
              stripe_product_id: showDetails?.stripe_product_id,
              ticket_url: showDetails?.ticket_url,
            })}
          />
          {stripeProductUrl && (
            <>
              <div className="mx-6 border-t border-border" />
              <a
                href={stripeProductUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-11 items-center gap-2.5 px-6 text-sm font-normal text-foreground transition-colors hover:bg-muted"
              >
                <CreditCard className="size-3.5" />
                Stripe
              </a>
            </>
          )}
          <div className="mx-6 border-t border-border" />
          <CuoButton
            variant="ghost"
            label={showDetails?.published ? "Unpublish" : "Publish"}
            icon={
              showDetails?.published ? (
                <EyeOff className="size-3.5" />
              ) : (
                <Eye className="size-3.5" />
              )
            }
            onClick={handleTogglePublish}
            disableOnAsync={isPublishing}
            className="h-11 w-full justify-start gap-2.5 rounded-none px-6 font-normal"
          />
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <div className="flex flex-col gap-2.5 border-b border-border bg-card/60 px-5 pt-5 backdrop-blur-[2px]">
      <button
        type="button"
        onClick={() => navigate("/admin/shows")}
        className="inline-flex w-fit items-center gap-1 text-[12.5px] leading-none text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" />
        Back to Shows
      </button>

      {/* Mobile: title + actions trigger on one row, date/badge on its own row below */}
      <div className="flex flex-col gap-2.5 md:hidden">
        <div className="flex items-center justify-between gap-3">
          {titleContent}
          {mobileActionsSheet}
        </div>
        {dateAndBadgeContent}
      </div>

      {/* Desktop: title + date/badge grouped on the left, actions on the right */}
      <div className="hidden items-center justify-between gap-3 md:flex">
        <div className="flex min-w-0 items-center gap-3">
          {titleContent}
          {dateAndBadgeContent}
        </div>
        {!showDetails?.historical && (
          <div className="flex flex-none items-center gap-2">
            <PopOverLinkCopyButton
              title="Link"
              url={publicShowPurchaseUrl({
                slug: showDetails?.slug,
                parent_slug: showDetails?.parent_slug,
                id: showDetails?.id,
                stripe_product_id: showDetails?.stripe_product_id,
                ticket_url: showDetails?.ticket_url,
              })}
            />
            {stripeProductUrl && (
              <a
                href={stripeProductUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-[30px] items-center gap-1.5 rounded-lg border border-input bg-card px-3 text-sm font-normal text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <CreditCard className="size-3.5" />
                Stripe
              </a>
            )}
            <CuoButton
              variant="outline"
              label={showDetails?.published ? "Unpublish" : "Publish"}
              icon={
                showDetails?.published ? (
                  <EyeOff className="size-3.5" />
                ) : (
                  <Eye className="size-3.5" />
                )
              }
              onClick={handleTogglePublish}
              disableOnAsync={isPublishing}
            />
          </div>
        )}
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => navigate(`/admin/shows/${showDetails.id}/${v}`, { replace: true })}
        className="mt-0"
      >
        <TabsList className="h-auto w-full justify-start gap-5 overflow-x-auto border-0 bg-transparent p-0">
          {visibleTabs.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className={`flex-none ${tabTriggerClassName}`}
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  );
}
